/**
 * EstimateEditor - Inline estimate builder with line items
 * T4: Estimate Builder UI
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Save,
  Loader2,
  DollarSign,
  Camera,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export interface EstimateLineItem {
  id?: string;
  description: string;
  category: string;
  quantity: number;
  unitPrice: number; // in cents
}

interface EstimateEditorProps {
  jobId: string;
  bookingId: string;
  initialLineItems?: EstimateLineItem[];
  initialNotes?: string;
  onSave: (lineItems: EstimateLineItem[], notes: string) => Promise<void>;
  onUploadPhoto?: () => void;
  isSaving?: boolean;
  readOnly?: boolean;
}

const CATEGORY_OPTIONS = [
  { value: 'labor', label: 'Mano de obra' },
  { value: 'parts', label: 'Recambios' },
  { value: 'materials', label: 'Materiales' },
  { value: 'other', label: 'Otros' },
];

const EMPTY_LINE: EstimateLineItem = {
  description: '',
  category: 'labor',
  quantity: 1,
  unitPrice: 0,
};

export function EstimateEditor({
  initialLineItems = [],
  initialNotes = '',
  onSave,
  onUploadPhoto,
  isSaving = false,
  readOnly = false,
}: EstimateEditorProps) {
  const [lineItems, setLineItems] = useState<EstimateLineItem[]>(
    initialLineItems.length > 0 ? initialLineItems : [{ ...EMPTY_LINE }]
  );
  const [notes, setNotes] = useState(initialNotes);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset when initial data changes
  useEffect(() => {
    if (initialLineItems.length > 0) {
      setLineItems(initialLineItems);
      setHasChanges(false);
    }
  }, [initialLineItems]);

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  const updateLine = useCallback((index: number, field: keyof EstimateLineItem, value: string | number) => {
    setLineItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setHasChanges(true);
  }, []);

  const addLine = useCallback(() => {
    setLineItems(prev => [...prev, { ...EMPTY_LINE }]);
    setHasChanges(true);
  }, []);

  const removeLine = useCallback((index: number) => {
    setLineItems(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    // Filter out empty lines
    const validLines = lineItems.filter(item => item.description.trim());
    if (validLines.length === 0) return;
    await onSave(validLines, notes);
    setHasChanges(false);
  }, [lineItems, notes, onSave]);

  const totalCents = lineItems.reduce((sum, item) => {
    return sum + Math.round(item.quantity * item.unitPrice);
  }, 0);

  const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

  return (
    <div className="bg-white rounded-lg border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-700">
          <DollarSign className="w-5 h-5" />
          <h3 className="font-medium">Presupuesto detallado</h3>
        </div>
        <div className="flex items-center gap-2">
          {onUploadPhoto && !readOnly && (
            <button
              onClick={onUploadPhoto}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border rounded-lg hover:bg-gray-50"
            >
              <Camera className="w-4 h-4" />
              Foto
            </button>
          )}
        </div>
      </div>

      {/* Line items table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 pr-2 font-medium" style={{ minWidth: 200 }}>Descripción</th>
              <th className="pb-2 px-2 font-medium" style={{ minWidth: 120 }}>Tipo</th>
              <th className="pb-2 px-2 font-medium text-right" style={{ minWidth: 70 }}>Cant.</th>
              <th className="pb-2 px-2 font-medium text-right" style={{ minWidth: 100 }}>Precio ud.</th>
              <th className="pb-2 px-2 font-medium text-right" style={{ minWidth: 100 }}>Total</th>
              {!readOnly && <th className="pb-2 pl-2 w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, index) => {
              const lineTotal = Math.round(item.quantity * item.unitPrice);
              return (
                <tr key={index} className="border-b border-gray-100 group">
                  <td className="py-1.5 pr-2">
                    {readOnly ? (
                      <span>{item.description}</span>
                    ) : (
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => updateLine(index, 'description', e.target.value)}
                        placeholder="Descripción del concepto..."
                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      />
                    )}
                  </td>
                  <td className="py-1.5 px-2">
                    {readOnly ? (
                      <span className="capitalize">{CATEGORY_OPTIONS.find(c => c.value === item.category)?.label || item.category}</span>
                    ) : (
                      <select
                        value={item.category}
                        onChange={e => updateLine(index, 'category', e.target.value)}
                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm bg-white"
                      >
                        {CATEGORY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="py-1.5 px-2">
                    {readOnly ? (
                      <span className="text-right block">{item.quantity}</span>
                    ) : (
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={e => updateLine(index, 'quantity', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="1"
                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-right"
                      />
                    )}
                  </td>
                  <td className="py-1.5 px-2">
                    {readOnly ? (
                      <span className="text-right block">{formatCurrency(item.unitPrice)}</span>
                    ) : (
                      <input
                        type="number"
                        value={(item.unitPrice / 100).toFixed(2)}
                        onChange={e => updateLine(index, 'unitPrice', Math.round(parseFloat(e.target.value || '0') * 100))}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-right"
                      />
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right font-medium">
                    {formatCurrency(lineTotal)}
                  </td>
                  {!readOnly && (
                    <td className="py-1.5 pl-2">
                      <button
                        onClick={() => removeLine(index)}
                        disabled={lineItems.length <= 1}
                        className={cn(
                          'p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors',
                          'opacity-0 group-hover:opacity-100',
                          lineItems.length <= 1 && 'invisible'
                        )}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-semibold">
              <td colSpan={readOnly ? 4 : 4} className="pt-3 text-right pr-2">
                Total:
              </td>
              <td className="pt-3 text-right text-lg text-green-700">
                {formatCurrency(totalCents)}
              </td>
              {!readOnly && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Add row & notes */}
      {!readOnly && (
        <>
          <button
            onClick={addLine}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="w-4 h-4" />
            Añadir línea
          </button>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas del presupuesto
            </label>
            <textarea
              value={notes}
              onChange={e => { setNotes(e.target.value); setHasChanges(true); }}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Notas adicionales..."
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges || lineItems.every(i => !i.description.trim())}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                'bg-blue-600 text-white hover:bg-blue-700',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar presupuesto
            </button>
          </div>
        </>
      )}
    </div>
  );
}
